/**
 * Horizontal bars with a value column — for long labels like partner names and communes.
 *
 * ⚠ A faithful port of `CS.charts.hBars` from the validated mockups. Same widths, same
 * rounding, same value column. The click behaviour below is added, not redesigned: the
 * geometry is untouched, and a bar with no `onSelect` renders exactly as it always did.
 */

import { formatNumber } from "@quality-field/shared";

import { CHART_PALETTE } from "@/components/charts/GroupedBars";

export interface HBarSegment {
  label: string;
  n: number;
  color: string;
}

export interface HBarRow {
  label: string;
  n: number;
  tone?: string;
  /** An explicit colour wins over a palette tone. Ignored when `segments` are given. */
  color?: string;
  /**
   * The bar's composition — drawn as one bar cut into coloured pieces rather than a solid
   * block. A single blue bar says how MANY; the segments say how many of WHAT, which is
   * the question somebody looking at a contractor actually has.
   *
   * ⚠ The segments must add up to `n`. They are widthed against `n`, so a set that does
   * not add up leaves a gap — a bar that quietly misstates what it contains. The `other`
   * bucket in `prioritySplit` exists precisely so that they always do.
   */
  segments?: HBarSegment[];
  /** What clicking this bar filters on. `null` is a real value — "the rows with none". */
  value?: string | null;
}

export function HBars({
  rows,
  onSelect,
  selected,
  emptyNote,
}: {
  rows: HBarRow[];
  /** Clicking a bar drills into it. Omitted where there is nothing to drill into. */
  onSelect?: (value: string | null, label: string) => void;
  /** The label currently drilled into; the others fade rather than disappear. */
  selected?: string | null;
  emptyNote?: string;
}) {
  const max = rows.reduce((m, r) => Math.max(m, r.n), 0);

  if (rows.length === 0) {
    return <p className="py-6 text-center text-xs text-slate-400">{emptyNote ?? "Nothing to show here."}</p>;
  }

  return (
    <>
      {rows.map((row) => {
        const pct = max ? (row.n / max) * 100 : 0;
        const color = row.color ?? CHART_PALETTE[row.tone ?? "slate"] ?? CHART_PALETTE.slate;
        const dimmed = selected != null && selected !== row.label;

        const parts = (row.segments ?? []).filter((s) => s.n > 0);

        const body = (
          <>
            <span className="w-32 shrink-0 truncate text-left text-xs text-slate-500" title={row.label}>
              {row.label}
            </span>
            <div className="h-5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
              {/* The bar's own width is its share of the biggest bar; inside it, each piece
                  takes its share of THIS bar. Two different denominators, deliberately:
                  the row compares rows, the pieces compare within a row. */}
              <div className="flex h-full overflow-hidden rounded-full" style={{ width: `${pct}%` }}>
                {parts.length > 0 ? (
                  parts.map((segment) => (
                    <div
                      key={segment.label}
                      className="h-full"
                      style={{ width: `${(segment.n / row.n) * 100}%`, background: segment.color }}
                      // ⚠ Every piece carries its own title. A priority with three rows out
                      // of four hundred is a couple of pixels wide, and hovering it is the
                      // only way to read a number the eye cannot measure.
                      title={`${row.label} · ${segment.label}: ${formatNumber(segment.n)}`}
                    />
                  ))
                ) : (
                  <div className="h-full w-full" style={{ background: color }} title={String(row.n)} />
                )}
              </div>
            </div>
            <span className="num w-12 shrink-0 text-right text-sm font-bold text-slate-800">
              {formatNumber(row.n)}
            </span>
          </>
        );

        return onSelect ? (
          <button
            key={row.label}
            type="button"
            onClick={() => onSelect(row.value ?? null, row.label)}
            title={selected === row.label ? "Click again to go back up" : `Drill into ${row.label}`}
            className={`flex w-full items-center gap-3 rounded-lg px-1 py-1.5 transition-colors hover:bg-slate-50 ${
              dimmed ? "opacity-40" : ""
            } ${selected === row.label ? "bg-slate-50" : ""}`}
          >
            {body}
          </button>
        ) : (
          <div key={row.label} className="flex items-center gap-3 px-1 py-1.5">
            {body}
          </div>
        );
      })}
    </>
  );
}
